import { Link } from "react-router-dom";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { CompaireLockup, CompaireIcon } from "@/components/brand";
import AuthModal from "@/components/auth/AuthModal";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import MarketingHeader, { type MarketingNavKey } from "@/components/marketing/MarketingHeader";

interface LandingHeaderProps {
  variant?: "main" | "agent" | "team";
  hideLogo?: boolean;
  /** When true, header is transparent at top of page and turns solid deep ink on scroll. Use for pages with dark hero sections. */
  transparentOnDarkHero?: boolean;
  /** When true, header is always dark-blurred (deep ink at 0.8 alpha + backdrop blur). Overrides transparentOnDarkHero scroll logic. */
  alwaysDarkNav?: boolean;
  /**
   * "marketing" renders the v3.3 marketing-site header (MarketingHeader) used
   * by the public marketing and legal routes. "legacy" — the default — keeps
   * this header unchanged for its other callers (the public organization
   * profile and news pages).
   */
  appearance?: "legacy" | "marketing";
  /** Marketing appearance only: the nav item for the current page. */
  active?: MarketingNavKey;
}

const LandingHeader = ({ appearance = "legacy", active, ...legacyProps }: LandingHeaderProps) =>
  appearance === "marketing" ? <MarketingHeader active={active} /> : <LegacyLandingHeader {...legacyProps} />;

const LegacyLandingHeader = ({
  variant = "main",
  hideLogo = false,
  transparentOnDarkHero = false,
  alwaysDarkNav = false,
}: Omit<LandingHeaderProps, "appearance" | "active">) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!transparentOnDarkHero || alwaysDarkNav) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparentOnDarkHero, alwaysDarkNav]);

  const onDark = transparentOnDarkHero || alwaysDarkNav;

  let headerBgClass: string;
  if (alwaysDarkNav) {
    headerBgClass = "border-b";
  } else if (transparentOnDarkHero) {
    headerBgClass = scrolled
      ? "bg-ink-900/95 backdrop-blur-sm border-b border-white/10"
      : "bg-transparent border-b border-transparent";
  } else {
    headerBgClass = "bg-card/95 backdrop-blur-sm border-b border-gray-100";
  }

  const navLinkClass = onDark
    ? "text-sm font-medium text-white/80 hover:text-white transition-colors"
    : "text-sm font-medium text-gray-600 hover:text-ink-900 transition-colors";
  const signInClass = onDark
    ? "text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-2"
    : "text-sm font-medium text-gray-600 hover:text-ink-900 transition-colors";
  const { user, isAuthenticated, signOut, isLoading } = useAuth();
  const { dashboardPath, isAdmin } = useUserRole();
  const { toast } = useToast();

  // Agent sign-up is open (founder decision F6, 2026-10-21): the CTA opens
  // AuthModal in sign-up mode, not the waitlist. The header owns the modal, so
  // the mobile CTA (inside `{mobileMenuOpen && …}`) survives the menu closing.
  const cta = { text: "Sign Up Free" };

  const openAuth = (mode: "signin" | "signup") => {
    setAuthMode(mode);
    setAuthModalOpen(true);
    setMobileMenuOpen(false);
  };
  const openSignIn = () => openAuth("signin");
  const openSignUp = () => openAuth("signup");

  const handleSignOut = async () => {
    await signOut();
    setMobileMenuOpen(false);
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
  };

  const getUserInitials = () => {
    if (!user) return "U";
    const name = user.user_metadata?.full_name || user.email || "";
    if (user.user_metadata?.full_name) {
      const parts = user.user_metadata.full_name.split(" ");
      return parts.map((p: string) => p[0]).join("").toUpperCase().slice(0, 2);
    }
    return user.email?.charAt(0).toUpperCase() || "U";
  };

  const getUserDisplayName = () => {
    if (!user) return "";
    return user.user_metadata?.full_name || user.email?.split("@")[0] || "User";
  };

  // Inline styles for alwaysDarkNav — v2 dark-band surface
  const alwaysDarkStyle: React.CSSProperties = alwaysDarkNav
    ? {
        background: "hsl(var(--ink-900) / 0.8)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottomColor: "rgba(255,255,255,0.06)",
      }
    : {};

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-colors duration-300 ${headerBgClass}`}
        style={{ top: 0, ...alwaysDarkStyle }}
      >
        <div
          className={alwaysDarkNav ? "px-10" : undefined}
          style={alwaysDarkNav ? undefined : { maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}
        >
          <div className={`flex items-center justify-between ${alwaysDarkNav ? "h-16" : "h-16 lg:h-20"}`}>
            {/* Logo */}
            {!hideLogo ? (
              <Link to="/" className="flex items-center gap-2.5">
                {/* Desktop lockup (icon mark + wordmark together) */}
                <CompaireLockup
                  className="landing-logo-desktop"
                  style={onDark ? { color: "#FFFFFF" } : undefined}
                />
                {/* Mobile icon-only mark */}
                <CompaireIcon className="landing-logo-icon-only" />
                {alwaysDarkNav ? (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    color: "hsl(var(--featured))",
                    border: "1px solid hsl(var(--featured) / 0.4)",
                    padding: "2px 6px",
                    borderRadius: 3,
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}>
                    Beta
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded leading-none">
                    Beta
                  </span>
                )}
              </Link>
            ) : (
              <div className="w-24 lg:w-32" />
            )}

            {/* Desktop Navigation */}
            {alwaysDarkNav ? (
              <nav className="hidden lg:flex items-center" style={{ gap: 32 }}>
                {[
                  { label: "Agents", to: "/agents" },
                  { label: "Teams & Brokerages", to: "/teams-brokerages" },
                  { label: "Pricing", to: "/pricing" },
                ].map(({ label, to }) => (
                  <Link
                    key={to}
                    to={to}
                    style={{
                      color: "rgba(255,255,255,0.65)",
                      fontSize: 14,
                      fontWeight: 400,
                      textDecoration: "none",
                      fontFamily: "'Inter', sans-serif",
                      transition: "color 0.2s",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#fff")}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.65)")}
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            ) : (
              <nav className="hidden lg:flex items-center gap-8">
                <Link to="/agents" className={navLinkClass}>Agents</Link>
                <Link to="/teams-brokerages" className={navLinkClass}>Teams & Brokerages</Link>
                <Link to="/pricing" className={navLinkClass}>Pricing</Link>
              </nav>
            )}

            {/* Desktop CTA / User Menu */}
            <div className="hidden lg:flex items-center" style={{ gap: 12 }}>
              {isLoading ? (
                <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
              ) : isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                      <Avatar className="h-9 w-9 border-2 border-primary">
                        <AvatarFallback className="bg-ink-900 text-white text-sm font-medium">
                          {getUserInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-white max-w-[120px] truncate">
                        {getUserDisplayName()}
                      </span>
                      <ChevronDown className="h-4 w-4 text-white/60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border shadow-lg z-[100]">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-medium truncate">{getUserDisplayName()}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to={dashboardPath} className="flex items-center gap-2 cursor-pointer">
                        <LayoutDashboard className="h-4 w-4" />
                        {isAdmin ? "Organization" : "Dashboard"}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <User className="h-4 w-4" />
                        {isAdmin ? "Agent Profile" : "My Profile"}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleSignOut}
                      className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <button onClick={openSignIn} className={signInClass}>
                    Sign In
                  </button>
                  {alwaysDarkNav ? (
                    <button
                      type="button"
                      onClick={openSignUp}
                      style={{
                        background: "hsl(var(--primary))",
                        color: "hsl(var(--primary-foreground))",
                        fontSize: 14,
                        fontWeight: 700,
                        padding: "9px 20px",
                        borderRadius: 6,
                        border: "none",
                        cursor: "pointer",
                        letterSpacing: "0.01em",
                        boxShadow: "0 0 20px hsl(var(--primary) / 0.27)",
                        transition: "box-shadow 0.2s, transform 0.1s",
                        fontFamily: "'Inter', sans-serif",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.boxShadow = "0 0 32px hsl(var(--primary) / 0.53)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.boxShadow = "0 0 20px hsl(var(--primary) / 0.27)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      {cta.text}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={openSignUp}
                      className={cn(
                        buttonVariants(),
                        "bg-primary hover:bg-primary-hover text-primary-foreground font-semibold h-10 px-6"
                      )}
                    >
                      {cta.text}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2 ${onDark ? "text-white" : ""}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              className="lg:hidden py-4 border-t animate-fade-in"
              style={alwaysDarkNav ? { borderColor: "rgba(255,255,255,0.08)" } : { borderColor: "hsl(var(--border))" }}
            >
              <nav className="flex flex-col gap-4">
                <Link
                  to="/agents"
                  className={onDark ? "text-base font-medium text-white/80 hover:text-white" : "text-base font-medium text-gray-600 hover:text-ink-900"}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Agents
                </Link>
                <Link
                  to="/teams-brokerages"
                  className={onDark ? "text-base font-medium text-white/80 hover:text-white" : "text-base font-medium text-gray-600 hover:text-ink-900"}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Teams & Brokerages
                </Link>
                <Link
                  to="/pricing"
                  className={onDark ? "text-base font-medium text-white/80 hover:text-white" : "text-base font-medium text-gray-600 hover:text-ink-900"}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Pricing
                </Link>
                <div
                  className="pt-4 flex flex-col gap-3 border-t"
                  style={alwaysDarkNav ? { borderColor: "rgba(255,255,255,0.08)" } : { borderColor: "hsl(var(--border))" }}
                >
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center gap-3 pb-2">
                        <Avatar className="h-10 w-10 border-2 border-primary">
                          <AvatarFallback className="bg-ink-900 text-white text-sm font-medium">
                            {getUserInitials()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className={`text-sm font-medium ${onDark ? "text-white" : ""}`}>{getUserDisplayName()}</p>
                          <p className="text-xs text-white/50 truncate">{user?.email}</p>
                        </div>
                      </div>
                      <Link
                        to={dashboardPath}
                        className={onDark ? "text-base font-medium text-white/80 hover:text-white flex items-center gap-2" : "text-base font-medium text-gray-600 hover:text-ink-900 flex items-center gap-2"}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        {isAdmin ? "Organization" : "Dashboard"}
                      </Link>
                      <Link
                        to="/profile"
                        className={onDark ? "text-base font-medium text-white/80 hover:text-white flex items-center gap-2" : "text-base font-medium text-gray-600 hover:text-ink-900 flex items-center gap-2"}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <User className="h-4 w-4" />
                        {isAdmin ? "Agent Profile" : "My Profile"}
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="text-base font-medium text-red-500 text-left flex items-center gap-2"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={openSignIn}
                        className={onDark ? "text-base font-medium text-white/80 text-left" : "text-base font-medium text-gray-600 text-left"}
                      >
                        Sign In
                      </button>
                      <button
                        type="button"
                        onClick={openSignUp}
                        className={cn(
                          buttonVariants(),
                          "w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold"
                        )}
                      >
                        {cta.text}
                      </button>
                    </>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultMode={authMode}
        allowSignup
      />

      <style>{`
        .landing-logo-desktop   { display: inline-block; height: 24px; width: auto; }
        .landing-logo-icon-only { display: none; }
        @media (max-width: 860px) {
          .landing-logo-desktop   { display: none; }
          .landing-logo-icon-only { display: inline-block; height: 30px; width: auto; }
        }
      `}</style>
    </>
  );
};

export default LandingHeader;
